import React from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, MessagesSquare, FileText, Settings, LogOut, Home, FolderOpen, BookOpen, CreditCard, Activity, CalendarCheck } from "lucide-react";
import { MANUAL_URL } from "../../lib/api";
import { useUser } from "../../context/UserContext";
import Avatar from "../common/Avatar";
import portalLogo from "../../assets/portal-logo.png";

const CREDIT_SIGNUP_URL =
  "https://pulse.scorexer.com/portal-signUp/signup.jsp?id=Q29zV1BRbmJyZklhTG9ORW5IYkRQdz09";

const links = [
  { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/portal/invoices", label: "Invoices", icon: FileText },
  { to: "/portal/documents", label: "Documents", icon: FolderOpen },
  { to: "/portal/messages", label: "Messages", icon: MessagesSquare },
  { to: "/portal/credit-appointments", label: "Credit Repair Appointments", icon: CalendarCheck },
  { to: "/portal/books", label: "Books", icon: BookOpen },
  { to: "/portal/activity", label: "Activity", icon: Activity },
  { to: "/portal/profile", label: "Settings", icon: Settings },
];

export default function PortalLayout() {
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={portalLogo}
              alt="Rehabilitated For Life SSM"
              className="h-11 w-auto bg-white rounded-lg px-2 py-1 shadow-sm"
              data-testid="portal-logo"
            />
            <div className="leading-tight hidden sm:block">
              <div className="font-serif font-semibold">Client Portal</div>
              <div className="text-[10px] text-gold-light uppercase tracking-wider">
                Rehabilitated For Life SSM
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-gray-200 hover:text-gold">
              <Home className="h-4 w-4" /> Website
            </Link>
            <div className="flex items-center gap-2">
              <Avatar
                name={user?.name}
                src={user?.avatar}
                className="h-9 w-9 rounded-full bg-gold text-navy text-sm font-semibold"
              />
              <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-sm text-gray-200 hover:text-red-300"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-6 h-max min-w-0">
          <nav className="bg-white rounded-xl border border-gray-100 shadow-sm p-2 flex lg:flex-col gap-1 overflow-x-auto">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 lg:flex-none ${
                    isActive
                      ? "bg-navy text-white"
                      : "text-gray-600 hover:bg-cream"
                  }`
                }
              >
                <l.icon className="h-5 w-5" />
                <span className="hidden sm:inline">{l.label}</span>
              </NavLink>
            ))}
            <a
              href={CREDIT_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 lg:flex-none text-gray-600 hover:bg-cream"
              data-testid="portal-credit-signup-link"
            >
              <CreditCard className="h-5 w-5" />
              <span className="hidden sm:inline">Start Credit Repair</span>
            </a>
            <a
              href={MANUAL_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 lg:flex-none text-gray-600 hover:bg-cream"
              data-testid="portal-manual-link"
            >
              <BookOpen className="h-5 w-5" />
              <span className="hidden sm:inline">User Manual</span>
            </a>
          </nav>
        </aside>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
